import {
	Checkbox,
	ComboBox,
	FlexColumn,
	FlexRow,
	IconTooltip,
	Label,
	Modal,
	Separator,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {ClientContactFormValues, EmailUniqueValidator, useClientContactForm} from './hooks/useClientContactForm';
import {GuestUserField, LabelWithTooltipField} from './ClientContactModal.styles';
import ProjectAccessField from './ProjectAccessField';
import ViewMoreLess from '@/components/view-more-less/view-more-less';

export type ClientContactModalMode = 'contact' | 'guest-user';
export type ClientContactModalEntryPoint = 'client-page' | 'header';
export type ClientOption = {id: string; name: string};
export type ProjectOption = {id: string; name: string; prefix?: string; color?: string};

export type ClientContactModalProps = {
	mode: ClientContactModalMode;
	entryPoint: ClientContactModalEntryPoint;
	clientId?: string;
	clientOptions: ClientOption[];
	projectOptions: ProjectOption[];
	isClientsLoading: boolean;
	isProjectsLoading: boolean;
	isGuestUser: boolean;
	onGuestUserToggle: () => void;
	onClientChange: (clientId: string) => void;
	onClose: () => void;
	onSubmit: (values: ClientContactFormValues) => void;
	isSubmitting?: boolean;
	validateEmailUnique?: EmailUniqueValidator;
};

const ClientContactModal = ({
	mode,
	clientId = '',
	clientOptions,
	projectOptions,
	isClientsLoading,
	isProjectsLoading,
	isGuestUser,
	onGuestUserToggle,
	onClientChange,
	onClose,
	onSubmit,
	isSubmitting = false,
	validateEmailUnique,
}: ClientContactModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const isContactMode = mode === 'contact';

	const {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		jobTitle,
		handleJobTitleChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		handleEmailBlur,
		phone,
		handlePhoneChange,
		handleGuestUserToggle,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		resetProjects,
		handleSubmit,
	} = useClientContactForm(projectOptions, isGuestUser, onGuestUserToggle, onSubmit, validateEmailUnique);

	const handleClientChange = (newClientId: string) => {
		resetProjects();
		onClientChange(newClientId);
	};

	const title = isContactMode
		? formatMessage({id: 'client_contact.create_modal.title'})
		: formatMessage({id: 'client_contact.guest_user.modal_title'});
	const primaryLabel = isContactMode
		? formatMessage({id: 'client_contact.create_modal.submit_button'})
		: formatMessage({id: 'client_contact.guest_user.submit_button'});

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={onClose}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				<div data-cy="client-contact-modal">
					<FlexColumn gap={'l'}>
						<LabelWithTooltipField>
							<ComboBox
								aria-label={formatMessage({id: 'client_contact.field.client'})}
								data-cy="client-contact-client-combobox"
								defaultItems={clientOptions}
								value={clientId}
								onChange={handleClientChange}
								disabled={isClientsLoading}
								placeholder={
									isClientsLoading
										? formatMessage({id: 'client_contact.placeholder.loading_clients'})
										: undefined
								}
								width={'flex'}
							>
								{item => (
									<ComboBox.Item textValue={item.name}>
										<Text noWrap>{item.name}</Text>
									</ComboBox.Item>
								)}
							</ComboBox>
							<FlexRow gap={'xs'} alignItems={'center'}>
								<Label>{formatMessage({id: 'client_contact.field.client'})}</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({id: 'client_contact.tooltip.client.title'})}
										details={formatMessage({id: 'client_contact.tooltip.client'})}
									/>
								</IconTooltip>
							</FlexRow>
						</LabelWithTooltipField>

						<FlexRow gap={'l'}>
							<TextField
								data-cy="client-contact-first-name-input"
								label={formatMessage({id: 'client_contact.field.first_name'})}
								value={firstName}
								onValueChange={handleFirstNameChange}
								hasError={!!firstNameError}
								errorText={firstNameError || undefined}
								size="large"
								width="flex"
							/>
							<TextField
								data-cy="client-contact-last-name-input"
								label={formatMessage({id: 'client_contact.field.last_name'})}
								value={lastName}
								onValueChange={handleLastNameChange}
								hasError={!!lastNameError}
								errorText={lastNameError || undefined}
								size="large"
								width="flex"
							/>
						</FlexRow>

						<TextField
							data-cy="client-contact-email-input"
							label={formatMessage({id: 'client_contact.field.email'})}
							value={email}
							onValueChange={handleEmailChange}
							onBlur={handleEmailBlur}
							hasError={!!emailError}
							errorText={emailError || undefined}
							size="large"
							width="flex"
						/>

						<ViewMoreLess
							expandLabel={formatMessage({id: 'client_contact.field.view_more'})}
							collapseLabel={formatMessage({id: 'client_contact.field.view_less'})}
						>
							<FlexColumn gap={'l'}>
								<TextField
									label={formatMessage({id: 'client_contact.field.job_title'})}
									value={jobTitle}
									onValueChange={handleJobTitleChange}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'client_contact.field.phone'})}
									value={phone}
									onValueChange={handlePhoneChange}
									size="large"
									width="flex"
								/>
								<Separator />
							</FlexColumn>
						</ViewMoreLess>

						<GuestUserField>
							<Checkbox
								checked={isGuestUser}
								onChange={handleGuestUserToggle}
								aria-label={formatMessage({id: 'client_contact.field.guest_user'})}
							/>
							<Label>{formatMessage({id: 'client_contact.field.guest_user'})}</Label>
							<IconTooltip>
								<IconTooltip.Item
									title={formatMessage({id: 'client_contact.field.guest_user'})}
									details={formatMessage({id: 'client_contact.tooltip.guest_user'})}
								/>
							</IconTooltip>
						</GuestUserField>

						{isGuestUser && (
							<ProjectAccessField
								availableProjects={availableProjects}
								selectedProjects={selectedProjects}
								isLoading={isProjectsLoading}
								onAdd={handleAddProject}
								onRemove={handleRemoveProject}
							/>
						)}
					</FlexColumn>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton dataCy="client-contact-submit-button" onPress={handleSubmit} disabled={isSubmitting}>
					{primaryLabel}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} disabled={isSubmitting}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ClientContactModal;
